import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { Timer, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getMyImpact } from '@/lib/api';

// Impact mark
const RewardIcon = ({ className }) => (
  <svg 
    viewBox="0 0 48.56 48.56" 
    className={className}
    fill="currentColor"
  >
    <circle cx="24.28" cy="24.28" r="24.28" fill="currentColor" opacity="0.2" />
    <path d="M24.01,26.04c1.47,0,2.66-1.19,2.66-2.66s-1.19-2.66-2.66-2.66-2.66,1.19-2.66,2.66,1.19,2.66,2.66,2.66Z" />
    <path d="M22.45,26.9h0c-.42,0-.41.41-.41.41v14.14c0,.28.23.51.51.51h3.16c.28,0,.51-.23.51-.51v-10.39c0-2.17-1.66-3.95-3.77-4.16Z" />
    <path d="M23.43,9.99c-7.02.44-12.79,6.03-13.41,13.01-.62,6.96,3.75,13,9.94,14.98v-7.62c-2.13-1.54-3.42-4.17-3.02-7.07.45-3.34,3.21-6.02,6.58-6.38,4.51-.49,8.32,3.02,8.32,7.42,0,2.62-1.36,4.93-3.42,6.26v7.5c5.97-1.75,10.33-7.25,10.33-13.77,0-8.23-6.95-14.85-15.31-14.33Z" />
  </svg>
);

// Time Tracker Component - Expandable
const TimeTrackerCard = () => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('today');
  const [sessionTime, setSessionTime] = useState(0);
  
  // Simulated time data (in minutes)
  const timeData = {
    today: { current: sessionTime, total: 45 + sessionTime },
    week: { current: sessionTime, total: 312 + sessionTime },
    month: { current: sessionTime, total: 1847 + sessionTime }
  };

  // Real Impact, not invented points.
  //
  // This used to be three hardcoded numbers described as "reward points based
  // on engagement" - the app paying people for time spent in it, on the four
  // busiest pages, next to a system built on the opposite idea. Numbers that
  // are not real are worse than no numbers, so these come from the ledger and
  // show nothing at all until it answers.
  const [impact, setImpact] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getMyImpact()
      .then(({ data }) => {
        if (cancelled) return;
        setImpact({
          today: data.daily?.total ?? 0,
          week: data.weekly?.total ?? 0,
          month: data.monthly?.total ?? 0,
        });
      })
      .catch(() => { /* a member with no Impact yet simply sees no figure */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Track session time
    const interval = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, []);

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours < 24) return `${hours}h ${mins}m`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    return `${days}d ${remainingHours}h`;
  };

  const tabs = [
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'This Week' },
    { id: 'month', label: 'This Month' }
  ];

  return (
    <div className="bg-white dark:bg-zinc-900/50 rounded-xl border border-stone-300 dark:border-white/5 overflow-hidden">
      {/* Header - Always visible, clickable to expand */}
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-3 flex items-center justify-between hover:bg-stone-50 dark:hover:bg-zinc-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-sprout-100 dark:bg-sprout-600/20 flex items-center justify-center">
            <Timer className="w-4 h-4 text-sprout-700 dark:text-sprout-400" />
          </div>
          <h3 className="font-semibold text-stone-800 dark:text-white text-sm">My Time On FamCentral</h3>
        </div>
        <div className="flex items-center gap-2">
          {/* Quick time preview when collapsed */}
          {!isExpanded && (
            <span className="text-xs font-medium text-sprout-700 dark:text-sprout-400">
              {formatTime(timeData.today.total)}
            </span>
          )}
          <ChevronDown className={cn(
            "w-4 h-4 text-stone-400 dark:text-zinc-500 transition-transform duration-200",
            isExpanded && "rotate-180"
          )} />
        </div>
      </button>
      
      {/* Expandable Content */}
      <div className={cn(
        "overflow-hidden transition-all duration-300 ease-in-out",
        isExpanded ? "max-h-[320px] opacity-100" : "max-h-0 opacity-0"
      )}>
        <div className="border-t border-stone-100 dark:border-white/5">
          {/* Toggle Tabs */}
          <div className="flex p-1.5 mx-3 mt-3 bg-stone-100 dark:bg-zinc-800 rounded-lg">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "flex-1 py-1.5 text-xs font-medium rounded-md transition-all",
                  activeTab === tab.id 
                    ? "bg-white dark:bg-zinc-700 text-stone-900 dark:text-white shadow-sm" 
                    : "text-stone-500 dark:text-zinc-400 hover:text-stone-700 dark:hover:text-zinc-300"
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
          
          {/* Time Display */}
          <div className="p-4 text-center">
            <div className="text-3xl font-bold text-stone-900 dark:text-white">
              {formatTime(timeData[activeTab].total)}
            </div>
            <p className="text-xs text-stone-500 dark:text-zinc-500 mt-1">
              {activeTab === 'today' && 'Total time today'}
              {activeTab === 'week' && 'Total time this week'}
              {activeTab === 'month' && 'Total time this month'}
            </p>
            
            {/* Session indicator */}
            <div className="flex items-center justify-center gap-1.5 mt-3">
              <span className="w-2 h-2 rounded-full bg-sprout-600 animate-pulse" />
              <span className="text-xs text-sprout-700 dark:text-sprout-400">
                Session: {sessionTime || '<1'}m
              </span>
            </div>
          </div>

          {/* Impact */}
          <div className="px-3 pb-4">
            {impact !== null && (
              <div className="bg-gathrr-50 dark:bg-gathrr-500/10 rounded-full px-4 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RewardIcon className="w-5 h-5 text-gathrr-600 dark:text-gathrr-400" />
                  <span className="text-sm font-medium text-gathrr-700 dark:text-gathrr-300">Impact</span>
                </div>
                <span className="text-lg font-bold text-gathrr-600 dark:text-gathrr-400">
                  {Number(impact[activeTab] || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })}
                </span>
              </div>
            )}

            {/* Goes somewhere now. It was a button with no handler. */}
            <button
              onClick={() => navigate('/impact')}
              className="w-full mt-2 text-center text-xs text-gathrr-600 dark:text-gathrr-400 hover:text-gathrr-700 dark:hover:text-gathrr-300 font-medium transition-colors"
            >
              See your Impact →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TRENDING_IDEAS = [
  { 
    id: 1, 
    content: "What if we created neighborhood micro-libraries in coffee shops?",
    author: "Elena M.",
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
    likes: 47,
    comments: 12
  },
  { 
    id: 2, 
    content: "Weekly community skill-sharing sessions could transform how we connect",
    author: "Marcus C.",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
    likes: 89,
    comments: 23
  },
  { 
    id: 3, 
    content: "Local artists should have dedicated spaces in public parks",
    author: "Priya S.",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop",
    likes: 156,
    comments: 34
  },
];

const ACTIVE_MEMBERS = [
  'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
  'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
  'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
  'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop',
  'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop',
  'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop',
];

const IN_THE_NEWS = [
  {
    id: 1,
    headline: "Local Teacher Raises $50K for Community Library",
    category: "Community",
    timeAgo: "2h",
    image: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=200&h=120&fit=crop",
    trending: true
  },
  {
    id: 2,
    headline: "Teen Inventor Wins International Science Award",
    category: "Innovation",
    timeAgo: "3h",
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=200&h=120&fit=crop",
    trending: true
  },
  {
    id: 3,
    headline: "Grandmother, 82, Launches App for Seniors",
    category: "Technology",
    timeAgo: "6h",
    image: "https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=200&h=120&fit=crop",
    trending: false
  },
];

export const RightSidebar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="hidden xl:flex flex-col w-80 space-y-3">
      {/* Time Tracker */}
      <TimeTrackerCard />

      {/* Recently Active Members */}
      <div className="bg-white dark:bg-zinc-900/50 rounded-xl border border-stone-300 dark:border-white/5 p-3">
        <h3 className="font-semibold text-stone-800 dark:text-white text-sm mb-3">Recently Active Members</h3>
        <div className="flex flex-wrap gap-2">
          {ACTIVE_MEMBERS.map((avatar, i) => (
            <Avatar key={i} className="w-10 h-10 cursor-pointer hover:ring-2 hover:ring-stone-400 dark:hover:ring-zinc-500 transition-all">
              <AvatarImage src={avatar} />
              <AvatarFallback className="bg-stone-100 dark:bg-zinc-700 text-stone-700 dark:text-zinc-300">U</AvatarFallback>
            </Avatar>
          ))}
        </div>
      </div>
    </div>
  );
};
